/**
 * ChordPro parsing utilities.
 * Handles detection, parsing, and chord definition extraction.
 */

import { type ChordDefinition, getBuiltInChord } from "@bandwith/chords";
import type {
  ChordDefinition as ChordSheetJsChordDef,
  Paragraph,
  Song,
} from "chordsheetjs";
import { Chord, ChordProParser, Tag } from "chordsheetjs";

export type ParsedChordPro = {
  song: Song;
  chordDefinitions: ChordDefinition[];
};

/**
 * Detect if text looks like ChordPro format.
 * Checks for:
 * - Directives like {title: ...} or {key: ...}
 * - Inline chords like [Am] or [G7]
 */
export function looksLikeChordPro(text: string): boolean {
  // Check for directives: {something} or {something: value}
  const hasDirective = /\{[a-z_]+(?::[^}]*)?\}/i.test(text);
  // Check for inline chords: [Chord]
  const hasInlineChord = /\[[A-G][#b]?[^[\]]*\]/i.test(text);
  return hasDirective || hasInlineChord;
}

/**
 * Convert ChordSheetJS ChordDefinition to our component's format
 */
function convertChordDef(def: ChordSheetJsChordDef): ChordDefinition {
  return {
    name: def.name,
    baseFret: def.baseFret,
    // Convert Fret type (number | '0' | '-1' | 'N' | 'x') to our format (number | 'x')
    frets: def.frets.map((f) => {
      if (f === "x" || f === "N" || f === "-1") return "x";
      if (f === "0") return 0;
      return f;
    }),
    // Convert fingers (number[]) - chordsheetjs uses numbers, we use number | '-'
    fingers: def.fingers?.map((f) => (f === 0 ? "-" : f)),
  };
}

/**
 * Extract chord definitions from a parsed ChordPro song
 */
function extractChordDefinitions(song: Song): ChordDefinition[] {
  const defs = song.getChordDefinitions();
  return Object.values(defs).map(convertChordDef);
}

/**
 * Extract shorthand {chord: X} directives that don't have fret definitions.
 * These need to be looked up in the built-in chord library.
 */
function extractShorthandChordDirectives(text: string): string[] {
  // Matches {chord: X} where X is a single word (no whitespace/braces).
  // Full definitions use {define:} not {chord:}, so all matches are shorthand.
  const regex = /\{chord:\s*([^}\s]+)\s*\}/gi;
  return [...text.matchAll(regex)]
    .map((m) => m[1])
    .filter((name): name is string => name !== undefined);
}

/**
 * Get chord definitions from both parsed song and built-in library.
 * Shorthand {chord: X} directives are resolved from the built-in library.
 */
function getChordDefinitions(song: Song, rawText: string): ChordDefinition[] {
  // Get explicitly defined chords from the parsed song
  const explicitDefs = extractChordDefinitions(song);
  const explicitNames = new Set(explicitDefs.map((d) => d.name));

  // Find shorthand chord directives and look them up in the library
  const shorthandNames = extractShorthandChordDirectives(rawText);
  const libraryDefs: ChordDefinition[] = [];

  for (const name of shorthandNames) {
    // Skip if already explicitly defined
    if (explicitNames.has(name)) continue;

    const builtIn = getBuiltInChord(name);
    if (builtIn) {
      libraryDefs.push(builtIn);
      explicitNames.add(name); // Prevent duplicates
    }
  }

  return [...explicitDefs, ...libraryDefs];
}

/** Section start tags and their default labels */
const SECTION_LABELS: Record<string, string> = {
  start_of_chorus: "Chorus",
  start_of_bridge: "Bridge",
};

/**
 * Add default labels to unlabeled verses and choruses.
 * - Unlabeled verses get "Verse N" where N is their sequential number
 * - Unlabeled choruses get "Chorus"
 *
 * Mutates the song in place.
 */
function addDefaultSectionLabels(song: Song): void {
  let verseNumber = 0;

  for (const line of song.lines) {
    for (const item of line.items) {
      if (!(item instanceof Tag)) continue;
      if (item.value) continue;

      if (item.name === "start_of_verse") {
        verseNumber++;
        item.value = `Verse ${verseNumber}`;
      } else if (item.name in SECTION_LABELS) {
        item.value = SECTION_LABELS[item.name] ?? "";
      }
    }
  }
}

/**
 * Close unclosed chord brackets that start with a note letter (A-G).
 * Finds `[` followed by content with no `]` before the next `[`, newline, or end of line.
 * Extracts the chord name portion and inserts `]` after it, keeping lyrics outside.
 */
export function sanitizeChordPro(text: string): string {
  const chordName =
    /^[A-Ga-g][#b]?(?:(?:maj|min|dim|aug|sus|add|m|M)[0-9]{0,2})*[0-9]{0,2}(?:\/[A-Ga-g][#b]?)?/;
  return text.replace(
    /\[([A-Ga-g][^[\]\n{]*?)(?=\[|\n|$)/g,
    (_, content: string) => {
      const m = chordName.exec(content);
      if (m) {
        return `[${m[0]}]${content.slice(m[0].length)}`;
      }
      return `[${content}]`;
    },
  );
}

/**
 * Transpose a chord name by a number of semitones.
 * Returns null if the name isn't a recognizable chord.
 */
function transposeChordName(name: string, semitones: number): string | null {
  const chord = Chord.parse(name);
  if (!chord) return null;
  return chord.transpose(semitones).toString();
}

/**
 * Re-resolve chord diagrams for a transposed song.
 *
 * Diagrams are generated algorithmically from the chord name, so transposing
 * a diagram means transposing its name and looking the new name up in the
 * built-in library. Custom {define} fret shapes can't be shifted by semitones
 * (open strings move), so the library voicing for the transposed name is the
 * best available diagram.
 */
function transposeChordDefinitions(
  definitions: ChordDefinition[],
  semitones: number,
): ChordDefinition[] {
  const result: ChordDefinition[] = [];
  const seen = new Set<string>();

  for (const def of definitions) {
    const transposedName = transposeChordName(def.name, semitones);
    if (!transposedName || seen.has(transposedName)) continue;

    const builtIn = getBuiltInChord(transposedName);
    if (builtIn) {
      result.push(builtIn);
      seen.add(transposedName);
    }
  }

  return result;
}

/**
 * Parse ChordPro text and extract all relevant data.
 * Returns null if text doesn't look like ChordPro or parsing fails.
 *
 * @param transpose Number of semitones to shift chords by (default 0).
 */
export function parseChordProContent(
  text: string,
  transpose = 0,
): ParsedChordPro | null {
  if (!text || !looksLikeChordPro(text)) {
    return null;
  }

  try {
    const parser = new ChordProParser();
    const parsed = parser.parse(sanitizeChordPro(text));

    // Transpose returns a new song; chords and {key} metadata both shift.
    const song = transpose === 0 ? parsed : parsed.transpose(transpose);

    // Add default labels to unlabeled sections
    addDefaultSectionLabels(song);

    // Get chord definitions from both explicit defines and built-in library
    const chordDefinitions = getChordDefinitions(song, text);

    return {
      song,
      chordDefinitions:
        transpose === 0
          ? chordDefinitions
          : transposeChordDefinitions(chordDefinitions, transpose),
    };
  } catch {
    return null;
  }
}

/** Default labels for paragraph types without explicit labels (e.g., expanded {chorus} directives) */
const PARAGRAPH_TYPE_LABELS: Record<string, string> = {
  chorus: "Chorus",
  verse: "Verse",
  bridge: "Bridge",
};

/**
 * Get the display label for a paragraph.
 *
 * Uses the paragraph's label if present (set by addDefaultSectionLabels during parsing).
 * Falls back to a default label based on paragraph type for expanded sections
 * (e.g., {chorus} directive expands to a chorus paragraph without a label).
 */
export function getParagraphLabel(paragraph: Paragraph): string | undefined {
  return paragraph.label ?? PARAGRAPH_TYPE_LABELS[paragraph.type ?? ""];
}
